/**
 * useSystemCheck — media-diagnostics helpers for the Settings → Device check
 * page.
 *
 * Heavily based on iLearn-PHP's resources/js/diagnostics.js (the universal,
 * non-100ms portions). The "one immovable constraint" framing applies:
 * a web page can never grant itself camera/microphone permission. We can
 * only detect, explain, and guide.
 *
 * What's here (framework-agnostic — no Vue imports, no DOM ownership):
 *   - detectEnvironment()         OS + browser UA sniff + secure-context check
 *   - MEDIA_ERROR_MAP             getUserMedia error name → title + remediation
 *   - describeMediaError(err)     lookup with safe fallback
 *   - enumerateAudioDevices()     count microphones and speakers (no labels
 *                                 until granted; that's the browser policy)
 *   - playArpeggio({ context })   self-contained speaker test — a looping
 *                                 C-E-G-C triangle-wave arpeggio via WebAudio,
 *                                 no asset, no network. Returns a stop() fn.
 *   - openMicStream()             requestUserMedia({ audio: true }) wrapped to
 *                                 throw a typed object instead of a raw DOMException
 *                                 so callers can switch on errorName cleanly
 *   - createMicLevelMeter(stream) WebAudio AnalyserNode → callback with 0..100
 *                                 level. Returns a stop() fn that releases the
 *                                 analyser and source.
 *   - troubleshootingFor(env)     browser-/OS-detected step-by-step instructions
 *                                 for fixing a microphone block, plus a manual
 *                                 picker if the detection is wrong.
 *   - collectDiagnostics(state)   assembles the JSON blob for "Copy diagnostics"
 */

// ─────────────────────────────────────────────────────────────────────
// Environment detection
// ─────────────────────────────────────────────────────────────────────

export function detectEnvironment() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";

  let browser = "Unknown";
  // Edge before Chrome (Edg/ token appears alongside Chrome/)
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/OPR\//.test(ua) || /Opera/.test(ua)) browser = "Opera";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Chrome\//.test(ua)) browser = "Chrome";
  else if (/Safari\//.test(ua)) browser = "Safari";

  let os = "Unknown";
  if (/Windows/.test(ua)) os = "Windows";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Linux/.test(ua)) os = "Linux";

  // In-app browser sniff. These embed-style browsers often block getUserMedia
  // entirely; the only remedy is "open in Safari/Chrome".
  let isInAppBrowser = false;
  let inAppBrowserName = null;
  if (/FBAN|FBAV/.test(ua)) { isInAppBrowser = true; inAppBrowserName = "Facebook"; }
  else if (/Instagram/.test(ua)) { isInAppBrowser = true; inAppBrowserName = "Instagram"; }
  else if (/Line\//.test(ua)) { isInAppBrowser = true; inAppBrowserName = "LINE"; }
  else if (/GSA\//.test(ua)) { isInAppBrowser = true; inAppBrowserName = "Google Search"; }

  return {
    browser,
    os,
    isInAppBrowser,
    inAppBrowserName,
    isSecureContext: typeof window !== "undefined" && window.isSecureContext === true,
    isMobile: /Android|iPhone|iPad|iPod/.test(ua),
    ua
  };
}

// ─────────────────────────────────────────────────────────────────────
// Media-error map (lifted from iLearn diagnostics.js verbatim — these
// strings are universally true for any browser-based voice/video app)
// ─────────────────────────────────────────────────────────────────────

export const MEDIA_ERROR_MAP = {
  NotAllowedError: {
    title: "Permission blocked",
    remediation:
      "Your browser is blocking access. Click the lock or microphone icon next to the address bar, choose Allow, then run the check again."
  },
  PermissionDeniedError: {
    title: "Permission blocked",
    remediation:
      "Your browser is blocking access. Click the lock or microphone icon next to the address bar, choose Allow, then run the check again."
  },
  NotFoundError: {
    title: "No microphone found",
    remediation:
      "No microphone was detected. Check it is plugged in and enabled in your operating system sound settings, then run the check again."
  },
  DevicesNotFoundError: {
    title: "No microphone found",
    remediation:
      "No microphone was detected. Check it is plugged in and enabled in your operating system sound settings, then run the check again."
  },
  NotReadableError: {
    title: "Microphone is in use by another app",
    remediation:
      "Another application is using your microphone. Close other voice or video apps (Zoom, Teams, Meet, FaceTime, Discord), then run the check again. This is the most common cause on shared computers."
  },
  TrackStartError: {
    title: "Microphone is in use by another app",
    remediation:
      "Another application is using your microphone. Close other voice apps and run the check again."
  },
  OverconstrainedError: {
    title: "Microphone cannot meet requirements",
    remediation:
      "The selected microphone could not provide a usable stream. Try a different microphone if you have one."
  },
  SecurityError: {
    title: "Blocked by browser security",
    remediation:
      "Access was blocked. Make sure you are on the secure (https) site — app.i-recall.au — and that your browser policy permits microphone use."
  },
  AbortError: {
    title: "Microphone could not start",
    remediation:
      "The device could not be started. Unplug and replug it (or restart your device), then run the check again."
  }
};

export function describeMediaError(err) {
  const name = (err && err.name) || "Error";
  return (
    MEDIA_ERROR_MAP[name] || {
      title: "Could not access microphone",
      remediation:
        `Something went wrong accessing the microphone (${name}). Try a different browser or device, then contact support if it persists.`
    }
  );
}

// ─────────────────────────────────────────────────────────────────────
// Device enumeration (counts only; labels require a granted stream)
// ─────────────────────────────────────────────────────────────────────

export async function enumerateAudioDevices() {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return { microphones: 0, speakers: 0, supported: false };
  }
  try {
    const list = await navigator.mediaDevices.enumerateDevices();
    return {
      microphones: list.filter((d) => d.kind === "audioinput").length,
      speakers: list.filter((d) => d.kind === "audiooutput").length,
      supported: true
    };
  } catch {
    return { microphones: 0, speakers: 0, supported: false };
  }
}

// ─────────────────────────────────────────────────────────────────────
// Speaker test — self-contained looping arpeggio
// (lifted from iLearn diagnostics.js, lightly tightened)
// ─────────────────────────────────────────────────────────────────────

const ARPEGGIO_NOTES = [523.25, 659.25, 783.99, 1046.5]; // C-E-G-C
const NOTE_LEN_S = 0.32;

/**
 * Plays a looping C-major arpeggio via WebAudio. No audio asset, no network.
 * Returns { stop() } — call to halt playback and release the AudioContext.
 */
export function playArpeggio() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) {
    return { stop() {} };
  }
  const ctx = new Ctx();

  function playPhrase(startAt) {
    ARPEGGIO_NOTES.forEach((freq, i) => {
      const t = startAt + i * NOTE_LEN_S;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      // Soft attack + decay so it fades in/out rather than blasting.
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.25, t + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + NOTE_LEN_S);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + NOTE_LEN_S);
    });
  }

  playPhrase(ctx.currentTime + 0.05);
  const phraseMs = ARPEGGIO_NOTES.length * NOTE_LEN_S * 1000;
  const timer = setInterval(() => {
    if (ctx.state !== "closed") playPhrase(ctx.currentTime + 0.02);
  }, phraseMs);

  return {
    stop() {
      clearInterval(timer);
      ctx.close().catch(() => {});
    }
  };
}

// ─────────────────────────────────────────────────────────────────────
// Microphone — open the stream and measure level
// ─────────────────────────────────────────────────────────────────────

/**
 * Opens an audio-only MediaStream. Throws an object with shape
 * { name, message } that callers can pass straight to describeMediaError().
 */
export async function openMicStream() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw { name: "SecurityError", message: "getUserMedia is unavailable in this browser" };
  }
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (err) {
    // Re-throw with a stable shape so the caller doesn't depend on DOMException.
    throw { name: err.name || "Error", message: err.message || String(err) };
  }
}

/**
 * Wires up a 0..100 level meter on top of an active MediaStream.
 * `onLevel` is called frequently (every animation frame) with a number 0..100.
 * Returns { stop() } — call to disconnect the analyser and stop the stream's tracks.
 */
export function createMicLevelMeter(stream, onLevel) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = 0.8;
  source.connect(analyser);

  const data = new Uint8Array(analyser.frequencyBinCount);
  let raf = 0;

  function tick() {
    analyser.getByteFrequencyData(data);
    // Average across bins, scale to 0..100. Voice bands sit ~0..150 in this
    // representation; map 0..130 to 0..100 with a small floor so the meter
    // shows minor ambient noise as a flicker rather than dead silence.
    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += data[i];
    const avg = sum / data.length;
    const level = Math.min(100, Math.round((avg / 130) * 100));
    onLevel(level);
    raf = requestAnimationFrame(tick);
  }
  raf = requestAnimationFrame(tick);

  return {
    stop() {
      cancelAnimationFrame(raf);
      try { source.disconnect(); } catch {}
      try { analyser.disconnect(); } catch {}
      try { ctx.close(); } catch {}
      try { stream.getTracks().forEach((t) => t.stop()); } catch {}
    }
  };
}

// ─────────────────────────────────────────────────────────────────────
// Troubleshooting content — per-browser remedies
// ─────────────────────────────────────────────────────────────────────

export const TROUBLESHOOTING = {
  "Chrome/Desktop": {
    title: "Chrome on desktop",
    steps: [
      "Click the lock icon (or the microphone icon if you've previously blocked the site) immediately to the left of the URL in the address bar.",
      'In the menu that opens, find Microphone and switch it to "Allow".',
      "Refresh this page. The browser will re-ask, or it will be allowed automatically — either is fine."
    ],
    notes: [
      "If you can't see a microphone or lock icon, go to Chrome → Settings → Privacy and security → Site settings → Microphone, find app.i-recall.au in the list, and allow it there."
    ]
  },
  "Edge/Desktop": {
    title: "Microsoft Edge on desktop",
    steps: [
      "Click the lock icon to the left of the URL in the address bar.",
      'Choose "Permissions for this site" → Microphone → Allow.',
      "Refresh the page."
    ],
    notes: [
      "Alternative path: edge://settings/content/microphone — find app.i-recall.au and set it to Allow."
    ]
  },
  "Firefox/Desktop": {
    title: "Firefox on desktop",
    steps: [
      "Click the lock icon to the left of the URL.",
      'Find the "Use the microphone" row — click the "x" to clear any old "block" decision, OR click the toggle to switch it to allow.',
      "Refresh the page. Firefox will re-ask; click Allow."
    ],
    notes: [
      "Alternative path: Firefox menu → Settings → Privacy & Security → Permissions → Microphone → Settings. Find app.i-recall.au and remove its block entry."
    ]
  },
  "Safari/macOS": {
    title: "Safari on macOS",
    steps: [
      "From the Safari menu bar (top of the screen), choose Safari → Settings (or Preferences on older macOS).",
      "Go to the Websites tab → Microphone (in the left sidebar).",
      "Find app.i-recall.au in the list and set it to Allow.",
      "Reload this page."
    ],
    notes: [
      "If Safari is the only thing currently using your microphone and it's still blocked, also check System Settings → Privacy & Security → Microphone → make sure Safari is enabled there too."
    ]
  },
  "Safari/iOS": {
    title: "Safari on iPhone / iPad",
    steps: [
      "Open the iOS Settings app (the grey gear icon).",
      "Scroll down to Safari → Microphone → Allow (or Ask).",
      "While you're in Settings, also go to Privacy & Security → Microphone and make sure Safari is enabled in that list.",
      "Return to the browser and reload this page."
    ],
    notes: [
      "On iOS, microphone permission requires BOTH the per-site setting (in Safari) AND the app-level setting (in Privacy). It's easy to miss the second one."
    ]
  },
  "Chrome/Android": {
    title: "Chrome on Android",
    steps: [
      "Tap the lock icon to the left of the URL.",
      "Tap Permissions → Microphone → Allow.",
      "Reload this page.",
      "Also check Android Settings → Apps → Chrome → Permissions → Microphone → Allow."
    ],
    notes: [
      "On Android, microphone permission requires BOTH the per-site setting (in Chrome) AND the app-level setting (in Android Settings). It's easy to miss the second one."
    ]
  },
  "Firefox/Android": {
    title: "Firefox on Android",
    steps: [
      "Tap the lock icon, then Connection secure → More information → Permissions.",
      "Find Microphone and set it to Allow.",
      "Reload this page.",
      "Also check Android Settings → Apps → Firefox → Permissions → Microphone → Allow."
    ],
    notes: []
  },
  "InAppBrowser": {
    title: "You're inside another app's built-in browser",
    steps: [
      "Email apps, social apps, and messaging apps often open links inside their own built-in browser. These usually can't access your microphone.",
      "Look for a menu (often three dots or three lines) and choose \"Open in browser\" or \"Open in Safari/Chrome\".",
      "Or copy the page URL, paste it into Safari or Chrome directly, then sign in again."
    ],
    notes: [
      "This is the single most common cause of microphone failures on phones. Email apps in particular block almost all device permissions in their in-app browser."
    ]
  },
  "Generic": {
    title: "General troubleshooting",
    steps: [
      "Check the address bar starts with https:// — microphone access only works on secure sites.",
      "Try a different browser (Chrome, Firefox, Safari, Edge).",
      "Check no other app is using your microphone — close Zoom, Teams, Meet, etc.",
      "Check your operating system's privacy settings allow your browser to use the microphone.",
      "If you're on a managed device (work, school), your IT administrator may have disabled microphone access — they need to allow it for app.i-recall.au."
    ],
    notes: []
  }
};

/**
 * Picks the most-applicable troubleshooting entry for the detected environment.
 * Caller can also override by supplying an explicit key.
 */
export function troubleshootingFor(env, override) {
  if (override && TROUBLESHOOTING[override]) return { key: override, ...TROUBLESHOOTING[override] };
  if (env.isInAppBrowser) return { key: "InAppBrowser", ...TROUBLESHOOTING.InAppBrowser };

  const platform = (env.os === "iOS" || env.os === "Android") ? env.os : "Desktop";
  const key = `${env.browser}/${platform}`;
  if (TROUBLESHOOTING[key]) return { key, ...TROUBLESHOOTING[key] };
  return { key: "Generic", ...TROUBLESHOOTING.Generic };
}

export function troubleshootingKeys() {
  return Object.keys(TROUBLESHOOTING);
}

// ─────────────────────────────────────────────────────────────────────
// Collect everything for the "Copy diagnostics" button
// ─────────────────────────────────────────────────────────────────────

export function collectDiagnostics(state) {
  return {
    capturedAt: new Date().toISOString(),
    appName: "i-Recall",
    page: typeof location !== "undefined" ? location.href : null,
    environment: state.environment,
    devices: state.devices,
    speaker: state.speaker,
    microphone: state.microphone,
    ua: typeof navigator !== "undefined" ? navigator.userAgent : null
  };
}
